import { useQuery } from "@tanstack/react-query";
import { type AxiosPromise } from "axios";
import { type onESignActivity } from "common/signalR";
import { Loading, Panel } from "components/panels";
import Pagination from "pagination/components/pagination";
import React from "react";
import { Message } from "semantic-ui-react";

type UnregisterOnActivity = ReturnType<typeof onESignActivity>;

interface Props<T extends server.dto.Activity<any>> {
  activityKey: string
  itemId: number
  config: server.dto.ActivityPanelConfig

  fetchActivities: (page: number, perPage: number) => AxiosPromise<server.dto.ApiResponseWrapper<T[]>>
  renderActivity: (activity: T) => JSX.Element

  onActivityRegisterFn: (callback: (dto: T) => void) => UnregisterOnActivity
  onActivity: (activity: T) => {
    refreshActivities: boolean
  }

  noMarginTop?: boolean
}

const ActivityPanel = <T extends server.dto.Activity<any>>(props: Props<T>) => {
  const [page, setPage] = React.useState(1);
  const perPage = 10;

  const query = useQuery<server.dto.ApiResponseWrapper<T[]>>({
    queryKey: ["activityPanel", props.activityKey, props.itemId, page],
    queryFn: async () => props.fetchActivities(page, perPage).then(r => r.data)
  });

  React.useEffect(() => {
    const res = props.onActivityRegisterFn(activity => {
      if (activity.itemId !== props.itemId) return;

      const result = props.onActivity(activity);
      if (result.refreshActivities) {
        query.refetch();
      }
    });

    return () => {
      res.removeEvent();
    };
  }, [page, props.itemId]);

  return <Panel
    title={props.config.headerLabel}
    style={{ marginTop: props.noMarginTop ? 0 : undefined }}
    panelClassName="s1_related-feature-panel"
    isLoading={query.isLoading}
    hasData={query.data?.pagination.total > 0}
    pagination={<Pagination
      small
      onPaginate={(page) => { setPage(page); }}
      paginationData={{
        page,
        perPage,
        total: query.data?.pagination.total ?? 0,
        pageCount: query.data?.pagination.pageCount ?? 0,
        maxPerPage: perPage
      }}
    />}
  >
    {query.data != null && <div className="list-with-gaps gap-layout">
      <div className="list-with-gaps-body">
        {query.data.data.map(props.renderActivity)}
      </div>
    </div>}

    {query.isLoading && <Loading />}

    <Message color="blue" className="margin-top-large">
      {RESX.Activity.ActivityIsCleanedAfter12Months}
    </Message>

  </Panel>;
};

export default ActivityPanel;
